import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { normalizeString, unlockScroll } from '@theme/utilities';

/**
 * Lowercases, drops diacritics and keeps letters, digits and currency signs as space separated words, so
 * "Côte d'Ivoire" matches "cote" and "ivoire", and "$" or "usd" match a currency.
 * @param {string} text
 * @returns {string}
 */
function toWords(text) {
  return normalizeString(text)
    .replace(/[^\p{L}\p{N}\p{Sc}]+/gu, ' ')
    .trim();
}

/**
 * Whether an open should put the caret in the filter field. Keyboard and mouse opens do. A touch open would raise
 * the on-screen keyboard over the list before the shopper chose to type, so it focuses the current country instead.
 * @param {Event} [event] - The click on the trigger, if there was one.
 * @returns {boolean}
 */
function prefersTyping(event) {
  if (event instanceof MouseEvent) {
    // Enter or Space on a button fires a click with detail 0.
    if (event.detail === 0) return true;
    if ('pointerType' in event && typeof event.pointerType === 'string' && event.pointerType) {
      return event.pointerType === 'mouse';
    }
  }
  return window.matchMedia('(pointer: fine)').matches;
}

/**
 * Bada country, currency and language dialog (snippets/bada-localization-dialog.liquid).
 *
 * Country rows and language segments are submit buttons of the native localization form, so a choice reloads the
 * page in that country or language at once. This component adds what the native dialog lacks: the animation and
 * scroll lock of Horizon's DialogComponent, the country filter with one polite result count, arrow keys, focus on
 * the search field when it opens (on the current country for touch opens, so no on-screen keyboard covers the
 * list), and focus back on the button that opened it. Escape closes only this dialog, never the menu drawer below.
 *
 * Opened by `on:click="#BadaLocalization/open"` (snippets/bada-localization-pill.liquid) and by the cancelable
 * `bada:open-localization` document event ("Change country" in assets/bada-home-elements.js).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLFormElement} [form] - The localization form.
 * @property {HTMLElement} [searchField] - Wrapper of the filter field, hidden until this module runs.
 * @property {HTMLInputElement} [search] - The filter field.
 * @property {HTMLElement} [status] - Polite live region for the result count.
 * @property {HTMLElement} [empty] - Visible "no match" message.
 * @property {HTMLElement} [list] - The scrolling area that holds the country groups.
 * @property {HTMLElement[]} [groups] - Country groups ("Popular", "Other countries", or one unlabelled group).
 * @property {HTMLLIElement[]} [items] - Country rows, each with a `data-search` string.
 *
 * @extends {DialogComponent}
 */
class BadaLocalizationDialog extends DialogComponent {
  /** @type {AbortController | undefined} */
  #listeners;

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {{ item: HTMLLIElement, button: HTMLButtonElement, words: string }[]} */
  #rows = [];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #statusTimer;

  /** True while the dialog was opened by this component (and so animates and locks scroll). */
  #scripted = false;

  #submitting = false;

  /** Whether the next open puts focus in the filter field (false for touch opens). */
  #typeFirst = true;

  /** @type {AbortController | undefined} Armed between an Escape keydown in the dialog and its keyup. */
  #escapeKeyUp;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    document.addEventListener('bada:open-localization', this.#onOpenRequest, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.#onOpened, { signal });
    this.refs.dialog.addEventListener('close', this.#onClosed, { signal });
    this.refs.dialog.addEventListener('keydown', this.#onDialogKeyDown, { signal });
    this.refs.form?.addEventListener('submit', this.#onSubmit, { signal });
    this.refs.list?.addEventListener('keydown', this.#onListKeyDown, { signal });

    this.#prepare();

    // Opened by the native invoker command before this module ran.
    if (this.refs.dialog.open) window.addEventListener('focusin', this.#guardFocus, true);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepare();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = undefined;
    this.#escapeKeyUp?.abort();
    this.#escapeKeyUp = undefined;
    clearTimeout(this.#statusTimer);
    window.removeEventListener('focusin', this.#guardFocus, true);
  }

  /**
   * Opens the dialog from a trigger button and remembers that button for focus return.
   * @param {Event} [event] - The click on the trigger.
   */
  open(event) {
    // The trigger also carries a native `command="show-modal"` for the time before this module loads.
    event?.preventDefault();
    const trigger = event?.target;
    this.#opener = trigger instanceof HTMLElement ? trigger : null;
    this.#typeFirst = prefersTyping(event);
    this.showDialog();
  }

  /**
   * Closes the dialog from the close button.
   * @param {Event} [event] - The click on the close button.
   */
  close(event) {
    event?.preventDefault();
    if (this.#scripted) {
      this.closeDialog();
    } else {
      this.refs.dialog.close();
    }
  }

  showDialog() {
    window.addEventListener('focusin', this.#guardFocus, true);
    super.showDialog();
  }

  /**
   * Filters the country rows by name, ISO code, currency code, name or symbol, matching word starts.
   */
  filter() {
    const { search, empty, list } = this.refs;
    if (!search) return;

    const query = toWords(search.value);
    let shown = 0;

    for (const { item, words } of this.#rows) {
      const match = !query || words.includes(` ${query}`);
      item.hidden = !match;
      if (match) shown += 1;
    }

    // A group label stays only while its group has a matching country.
    for (const group of this.refs.groups ?? []) {
      group.hidden = !group.querySelector('li:not([hidden])');
    }

    if (empty) empty.hidden = shown > 0;
    if (list) list.scrollTop = 0;
    this.#announce(query ? shown : null);
  }

  /**
   * Keeps Enter in the filter from submitting the form's first button, and moves into the list with Arrow Down.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== 'ArrowDown') return;

    event.preventDefault();
    this.#visibleButtons()[0]?.focus();
  }

  #prepare() {
    this.#rows = (this.refs.items ?? []).flatMap((item) => {
      const button = item.querySelector('button');
      if (!(button instanceof HTMLButtonElement)) return [];
      return [{ item, button, words: ` ${toWords(item.dataset.search ?? '')}` }];
    });

    if (this.refs.searchField) this.refs.searchField.hidden = false;
  }

  /** @returns {HTMLButtonElement[]} */
  #visibleButtons() {
    return this.#rows.filter(({ item }) => !item.hidden).map(({ button }) => button);
  }

  /**
   * One polite announcement after typing settles, not one per keystroke.
   * @param {number | null} count - Rows shown, or null when the filter is empty.
   */
  #announce(count) {
    const { status } = this.refs;
    if (!status) return;

    clearTimeout(this.#statusTimer);
    this.#statusTimer = setTimeout(() => {
      if (count === null) {
        status.textContent = '';
      } else if (count === 0) {
        status.textContent = status.dataset.labelNone ?? '';
      } else {
        status.textContent = (status.dataset.labelCount ?? '').replace('[count]', String(count));
      }
    }, 400);
  }

  #resetFilter() {
    const { search, empty, status } = this.refs;
    clearTimeout(this.#statusTimer);
    if (status) status.textContent = '';
    if (!search || !search.value) return;

    search.value = '';
    for (const { item } of this.#rows) item.hidden = false;
    for (const group of this.refs.groups ?? []) group.hidden = false;
    if (empty) empty.hidden = true;
  }

  /** Scrolls the current country into the middle of the list. */
  #revealCurrent() {
    const { list } = this.refs;
    const current = list?.querySelector('[aria-current="true"]');
    if (!list || !(current instanceof HTMLElement)) return;

    const row = current.closest('li') ?? current;
    list.scrollTop = Math.max(0, row.offsetTop - (list.clientHeight - row.offsetHeight) / 2);
  }

  #onOpened = () => {
    this.#scripted = true;
    this.#revealCurrent();

    const { dialog, list, search, searchField } = this.refs;
    const filter = search && searchField && !searchField.hidden ? search : undefined;
    const current = list?.querySelector('[aria-current="true"]') ?? dialog.querySelector('[aria-current="true"]');

    /** @type {Element | undefined} */
    let target;
    if (this.#typeFirst) {
      target = filter ?? this.#visibleButtons()[0];
    } else {
      target = current ?? this.#visibleButtons()[0] ?? filter;
    }
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  };

  /**
   * Escape closes this dialog on keydown. The menu drawer (assets/header-drawer.js) closes on the Escape keyup, which
   * would otherwise land on the drawer button that focus has just returned to and close the drawer as well. Swallow
   * that one keyup.
   * @param {KeyboardEvent} event
   */
  #onDialogKeyDown = (event) => {
    if (event.key !== 'Escape' || this.#escapeKeyUp) return;

    const controller = new AbortController();
    this.#escapeKeyUp = controller;
    const disarm = () => {
      controller.abort();
      if (this.#escapeKeyUp === controller) this.#escapeKeyUp = undefined;
    };

    window.addEventListener(
      'keyup',
      (keyup) => {
        if (keyup.key !== 'Escape') return;
        keyup.stopPropagation();
        disarm();
      },
      { capture: true, signal: controller.signal }
    );
    // The keyup never arrives here if the window loses focus first; do not swallow a later, unrelated one.
    window.addEventListener('blur', disarm, { signal: controller.signal });
  };

  #onClosed = () => {
    window.removeEventListener('focusin', this.#guardFocus, true);
    // A native close (invoker command or Escape before this module ran) skips DialogComponent's own unlock.
    unlockScroll(this.refs.dialog);
    this.#scripted = false;

    if (!this.#submitting) this.#resetFilter();

    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected && opener.getClientRects().length > 0) {
      opener.focus({ preventScroll: true });
    }
  };

  /**
   * Opens the dialog for another script, for example "Change country" on the shipping page.
   * @param {Event} event
   */
  #onOpenRequest = (event) => {
    event.preventDefault();
    const active = document.activeElement;
    this.#opener = active instanceof HTMLElement && active !== document.body ? active : null;
    this.#typeFirst = prefersTyping();
    this.showDialog();
  };

  /**
   * Marks the chosen row while the page reloads; picking what is already active only closes the dialog.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (this.#submitting) {
      event.preventDefault();
      return;
    }

    const { submitter } = event;
    if (submitter instanceof HTMLButtonElement && submitter.getAttribute('aria-current') === 'true') {
      event.preventDefault();
      this.close();
      return;
    }

    // The submitter must stay enabled, or the browser leaves its country or language out of the request.
    this.#submitting = true;
    submitter?.setAttribute('data-pending', '');
    this.refs.form?.setAttribute('aria-busy', 'true');
  };

  /**
   * Back/forward cache restores the page as it was when the form was sent; make it usable again.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.#submitting = false;
    const { form } = this.refs;
    if (!form) return;

    form.removeAttribute('aria-busy');
    for (const button of form.querySelectorAll('[data-pending]')) button.removeAttribute('data-pending');
  };

  /**
   * Arrow keys move between the visible rows; Arrow Up on the first row returns to the filter.
   * @param {KeyboardEvent} event
   */
  #onListKeyDown = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLButtonElement)) return;

    const buttons = this.#visibleButtons();
    const index = buttons.indexOf(target);
    if (index === -1) return;

    const { search, searchField } = this.refs;
    const filter = search && searchField && !searchField.hidden ? search : undefined;

    /** @type {HTMLElement | undefined} */
    let next;
    switch (event.key) {
      case 'ArrowDown':
        next = buttons[index + 1];
        break;
      case 'ArrowUp':
        next = index === 0 ? filter : buttons[index - 1];
        break;
      case 'Home':
        next = buttons[0];
        break;
      case 'End':
        next = buttons[buttons.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    next?.focus();
  };

  /**
   * The menu drawer traps focus with a document-level capture listener (assets/focus.js). This dialog opens on top
   * of the drawer, so focus moving inside the dialog must not reach that trap.
   * @param {FocusEvent} event
   */
  #guardFocus = (event) => {
    if (event.target instanceof Node && this.refs.dialog.contains(event.target)) {
      event.stopPropagation();
    }
  };
}

if (!customElements.get('bada-localization-dialog')) {
  customElements.define('bada-localization-dialog', BadaLocalizationDialog);
}
